/**
 * Parse date string in various formats and return Date object
 * @param {string} dateStr - Date string in various formats
 * @returns {Date|null} - Parsed date or null if invalid
 */
const parseDate = (dateStr) => {
  if (!dateStr || dateStr === '-' || dateStr === 'No deadline' || dateStr === 'N/A') {
    return null;
  }
  
  try {
    let date;
    
    // Handle slash formats (DD/MM/YYYY or MM/DD/YYYY)
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        // Check if it's likely MM/DD/YYYY (month > 12 in first position means it's DD/MM/YYYY)
        const first = parseInt(parts[0]);
        const second = parseInt(parts[1]);
        const year = parseInt(parts[2]);
        
        if (first > 12) {
          // Must be DD/MM/YYYY
          date = new Date(year, second - 1, first);
        } else if (second > 12) {
          // Must be MM/DD/YYYY
          date = new Date(year, first - 1, second);
        } else {
          // Ambiguous - try MM/DD/YYYY first (US format), then DD/MM/YYYY
          date = new Date(year, first - 1, second);
          if (isNaN(date.getTime())) {
            date = new Date(year, second - 1, first);
          }
        }
      }
    } 
    // Handle dot format (DD.MM.YYYY)
    else if (dateStr.includes('.')) {
      const parts = dateStr.split('.');
      if (parts.length === 3) {
        date = new Date(parts[2], parts[1] - 1, parts[0]);
      }
    }
    // Handle dash format (YYYY-MM-DD)
    else if (dateStr.includes('-')) {
      date = new Date(dateStr);
    } 
    // Try parsing as-is
    else {
      date = new Date(dateStr);
    }
    
    if (!date || isNaN(date.getTime())) {
      return null;
    }
    
    return date;
  } catch {
    return null;
  }
};

/**
 * Format date to dd.mm.yyyy format
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} - Formatted date string (dd.mm.yyyy)
 */
export const formatDate = (dateInput) => {
  if (!dateInput || dateInput === '-' || dateInput === 'No deadline' || dateInput === 'N/A') {
    return dateInput || '-';
  }
  
  try {
    let date;
    if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      date = parseDate(dateInput);
    }
    
    if (!date || isNaN(date.getTime())) {
      return dateInput; // Return original if can't parse
    }
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}.${month}.${year}`;
  } catch {
    return dateInput;
  }
};

/**
 * Utility function to get color class for deadline based on time remaining
 * @param {string} deadlineStr - Deadline string in various formats
 * @returns {string} - Tailwind color class
 */
export const getDeadlineColor = (deadlineStr) => {
  const deadlineDate = parseDate(deadlineStr);
  
  if (!deadlineDate) {
    return 'text-gray-600 dark:text-gray-400';
  }
  
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  deadlineDate.setHours(0, 0, 0, 0);
  
  const diffDays = Math.floor((deadlineDate - now) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return 'text-red-600 dark:text-red-400';        // Overdue
  if (diffDays <= 7) return 'text-orange-600 dark:text-orange-400'; // Within a week
  return 'text-green-600 dark:text-green-400';                      // More than a week
};
